import React, { useReducer, useCallback, useMemo } from "react";
import bloqueosContext from "./bloqueosContext";
import bloqueosReducer from "./bloqueosReducer";
import clienteAxios from "../../config/axios";
import Swal from "sweetalert2";

const BloqueosState = (props) => {
  const initialState = {
    bloqueosList: null,
    loading: false,
    saving: false,
    error: null,
  };

  const [state, dispatch] = useReducer(bloqueosReducer, initialState);

  // Obtener todos los bloqueos activos
  const obtenerBloqueosActivos = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const respuesta = await clienteAxios.get("/cancha-bloqueos?estado=ACTIVO");
      dispatch({
        type: "OBTENER_BLOQUEOS",
        payload: respuesta.data.data,
      });
    } catch (error) {
      console.error("Error al obtener bloqueos:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al obtener bloqueos",
      });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  // Crear bloqueo (uno o múltiples)
  const crearBloqueo = useCallback(async (bloqueoData) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      const respuesta = await clienteAxios.post("/cancha-bloqueos", bloqueoData);
      dispatch({
        type: "AGREGAR_BLOQUEO",
        payload: respuesta.data.data,
      });
      Swal.fire({
        icon: "success",
        title: "Bloqueo creado",
        text: "El bloqueo se creó exitosamente",
        timer: 2000,
        customClass: {
          container: 'swal-high-z'
        }
      });
      return respuesta.data.data;
    } catch (error) {
      console.error("Error al crear bloqueo:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al crear bloqueo",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al crear bloqueo",
        customClass: {
          container: 'swal-high-z'
        }
      });
      throw error;
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  // Crear múltiples bloqueos
  const crearBloqueosMultiples = useCallback(async (canchas, configBloqueo) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      const promesas = canchas.map((cancha) =>
        clienteAxios.post("/cancha-bloqueos", {
          activoID: cancha._id,
          codActivo: cancha.codActivo,
          nameActivo: cancha.name,
          ...configBloqueo,
        })
      );

      const respuestas = await Promise.all(promesas);
      const bloqueosCreados = respuestas.map((r) => r.data.data);

      dispatch({
        type: "AGREGAR_BLOQUEOS_MULTIPLES",
        payload: bloqueosCreados,
      });

      Swal.fire({
        icon: "success",
        title: "Bloqueos creados",
        text: `Se crearon ${bloqueosCreados.length} bloqueo(s) exitosamente`,
        timer: 2000,
        customClass: {
          container: 'swal-high-z'
        }
      });

      return bloqueosCreados;
    } catch (error) {
      console.error("Error al crear bloqueos múltiples:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al crear bloqueos",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al crear bloqueos",
        customClass: {
          container: 'swal-high-z'
        }
      });
      throw error;
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  // Cancelar bloqueo
  const cancelarBloqueo = useCallback(async (bloqueoID) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      const respuesta = await clienteAxios.put(`/cancha-bloqueos/${bloqueoID}/cancelar`);
      dispatch({
        type: "ACTUALIZAR_BLOQUEO",
        payload: respuesta.data.data,
      });
      return respuesta.data.data;
    } catch (error) {
      console.error("Error al cancelar bloqueo:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al cancelar bloqueo",
      });
      throw error;
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  // Completar bloqueo
  const completarBloqueo = useCallback(async (bloqueoID) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      const respuesta = await clienteAxios.put(`/cancha-bloqueos/${bloqueoID}/completar`);
      dispatch({
        type: "ACTUALIZAR_BLOQUEO",
        payload: respuesta.data.data,
      });
      return respuesta.data.data;
    } catch (error) {
      console.error("Error al completar bloqueo:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al completar bloqueo",
      });
      throw error;
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  // Eliminar bloqueo
  const eliminarBloqueo = useCallback(async (bloqueoID) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      await clienteAxios.delete(`/cancha-bloqueos/${bloqueoID}`);
      dispatch({
        type: "ELIMINAR_BLOQUEO",
        payload: bloqueoID,
      });
    } catch (error) {
      console.error("Error al eliminar bloqueo:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al eliminar bloqueo",
      });
      throw error;
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  // Eliminar múltiples bloqueos
  const eliminarBloqueosMultiples = useCallback(async (bloqueosIDs) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      // Hacer todas las peticiones en paralelo
      const promesas = bloqueosIDs.map((id) =>
        clienteAxios.delete(`/cancha-bloqueos/${id}`)
      );

      await Promise.all(promesas);

      dispatch({
        type: "ELIMINAR_BLOQUEOS_MULTIPLES",
        payload: bloqueosIDs,
      });

      Swal.fire({
        icon: "success",
        title: "Bloqueos eliminados",
        text: `Se eliminaron ${bloqueosIDs.length} bloqueo(s) exitosamente`,
        timer: 2000,
        customClass: {
          container: 'swal-high-z'
        }
      });
    } catch (error) {
      console.error("Error al eliminar bloqueos múltiples:", error);
      dispatch({
        type: "SET_ERROR",
        payload: error.response?.data?.message || "Error al eliminar bloqueos",
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al eliminar bloqueos",
        customClass: {
          container: 'swal-high-z'
        }
      });
      throw error;
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  const value = useMemo(
    () => ({
      bloqueosList: state.bloqueosList,
      loading: state.loading,
      saving: state.saving,
      error: state.error,
      obtenerBloqueosActivos,
      crearBloqueo,
      crearBloqueosMultiples,
      cancelarBloqueo,
      completarBloqueo,
      eliminarBloqueo,
      eliminarBloqueosMultiples,
    }),
    [
      state.bloqueosList,
      state.loading,
      state.saving,
      state.error,
      obtenerBloqueosActivos,
      crearBloqueo,
      crearBloqueosMultiples,
      cancelarBloqueo,
      completarBloqueo,
      eliminarBloqueo,
      eliminarBloqueosMultiples,
    ]
  );

  return (
    <bloqueosContext.Provider value={value}>
      {props.children}
    </bloqueosContext.Provider>
  );
};

export default BloqueosState;
